import { useEffect, useRef } from "react";
import { RINGS, WORLD_CENTRE, WORLD_SIZE } from "@zend/content";
import { useWorld, VIEW_RADIUS } from "./state.js";
import type { WorldGroundBounds } from "./presentation.js";

const SIZE = 132;

/** Ring-coloured overview with the explored chunks, own base, bookmarks and the viewport box; tap to jump. */
export function Minimap({ centre, bounds = null }: { centre: { x: number; y: number }; bounds?: WorldGroundBounds | null }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const me = useWorld((s) => s.me);
  const view = useWorld((s) => s.view);
  const setJump = useWorld((s) => s.setJump);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const g = c.getContext("2d");
    if (!g) return;
    const k = SIZE / WORLD_SIZE;
    g.clearRect(0, 0, SIZE, SIZE);
    g.fillStyle = "#0b0f14";
    g.fillRect(0, 0, SIZE, SIZE);
    for (const r of [...RINGS].reverse()) {
      const outer = Math.min(r.maxDist, WORLD_CENTRE * Math.SQRT2) * k;
      g.beginPath();
      g.arc(WORLD_CENTRE * k, WORLD_CENTRE * k, outer, 0, Math.PI * 2);
      g.fillStyle = r.color + "33";
      g.fill();
    }
    if (view) {
      g.fillStyle = "rgba(255,255,255,0.10)";
      const chunk = view.chunk * k;
      for (const key of view.exploredChunks) {
        const [cx, cy] = key.split(",").map(Number);
        g.fillRect((cx ?? 0) * chunk, (cy ?? 0) * chunk, chunk, chunk);
      }
    }
    if (me) {
      for (const b of me.bookmarks) {
        g.fillStyle = "#22d3ee";
        g.fillRect(b.x * k - 1, b.y * k - 1, 3, 3);
      }
      g.fillStyle = "#f5c542";
      g.beginPath();
      g.arc(me.position.x * k, me.position.y * k, 2.6, 0, Math.PI * 2);
      g.fill();
    }
    g.strokeStyle = "rgba(255,255,255,0.75)";
    g.lineWidth = 1;
    if (bounds) g.strokeRect(bounds.minX * k, bounds.minY * k, (bounds.maxX - bounds.minX) * k, (bounds.maxY - bounds.minY) * k);
    else g.strokeRect((centre.x - VIEW_RADIUS) * k, (centre.y - VIEW_RADIUS) * k, VIEW_RADIUS * 2 * k, VIEW_RADIUS * 2 * k);
  }, [me, view, centre, bounds]);
  return (
    <canvas
      ref={ref}
      width={SIZE}
      height={SIZE}
      className="minimap glass"
      data-testid="minimap"
      onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setJump({ x: Math.round(((e.clientX - rect.left) / rect.width) * WORLD_SIZE), y: Math.round(((e.clientY - rect.top) / rect.height) * WORLD_SIZE) });
      }}
    />
  );
}
